import { observer } from 'mobx-react';
import Loader from '../../common/Loader';
import type { IJobApplication } from 'src/stores/JobApplicationStore/JobApplication';
import DetailsContentView from '../DetailsContentView';
import * as styles from './JobApplicationDetails.css';
import { JOB_APPLICATION_TAB } from '../../../constants/tabs';
import ActionButtons from './ActionButtons';
import ChangeBucketError from './ChangeBucketError';
import type { Bucket } from '../../../stores/JobApplicationStore/types';
import { Box, useToast } from 'braid-design-system';
import { useStores } from 'src/utils/hooks/useStores';
import useHandleStatusChange from '../../../App/hooks/useHandleStatusChange';
import { STATUS_CHANGE_LOCATION } from '../../../graphql/hirer/updateApplicationsStatus';
import { noop } from '../../type';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
interface Props {
  jobApplication: IJobApplication;
  onClose: () => void;
  isFromHirerActivity?: boolean;
  questionnaireId?: string;
  applicationCardIsExpanded?: boolean;
}

const JobApplicationDetails = ({
  jobApplication: {
    isInbox,
    isChangeStateError,
    isLoading,
    selectedTabInDetailView,
  },
  questionnaireId,
  onClose,
  jobApplication,
  isFromHirerActivity = false,
  applicationCardIsExpanded,
}: Props) => {
  const store = useStores();
  const { isMobile, jobApplicationStore } = store;
  const { locale } = useAppConfig();

  const showToast = useToast();
  const { handleStatusChange } = useHandleStatusChange();

  if (isLoading) {
    return <Loader />;
  }

  const onStatusChange = async (bucket: Bucket) => {
    const statusChangeLocation = STATUS_CHANGE_LOCATION.CANDIDATE_DETAIL;
    const changeParams = {
      store,
      jobApplicationStore,
      jobApplication,
      destinationBucket: bucket,
      statusChangeLocation,
      showToast,
      onUpdateSuccess: onClose,
      isExpanded: applicationCardIsExpanded,
    };
    if (isFromHirerActivity) {
      await handleStatusChange({
        ...changeParams,
        onUpdateSuccess: noop,
        showSuccessMessageToast: false,
      });
    } else {
      await handleStatusChange(changeParams);
    }
  };

  const statusActions = (
    <ActionButtons
      statusMenuPlacement={isMobile ? 'top' : 'bottom'}
      jobApplication={jobApplication}
      onStatusChange={onStatusChange}
    />
  );

  const withStickyButtons =
    isInbox &&
    isMobile &&
    selectedTabInDetailView === JOB_APPLICATION_TAB.PROFILE;
  return (
    <>
      {isChangeStateError && (
        <Box margin="xsmall">
          <ChangeBucketError />
        </Box>
      )}

      <>
        <Box
          className={{
            [styles.withStickyButton]: withStickyButtons,
          }}
          display="flex"
          flexDirection="column"
          flexGrow={1}
          paddingBottom={['xlarge', 'none', 'none']}
        >
          <DetailsContentView
            jobApplication={jobApplication}
            isMobile={isMobile}
            locale={locale}
            questionnaireId={questionnaireId}
          >
            {statusActions}
          </DetailsContentView>
        </Box>
        {isMobile &&
          selectedTabInDetailView === JOB_APPLICATION_TAB.PROFILE && (
            <div
              className={`
              ${
                isInbox
                  ? styles.stickyInboxActionContainer
                  : styles.statusDropdownContainer
              }
            `}
            >
              <Box marginBottom="medium" padding="xxsmall">
                {statusActions}
              </Box>
            </div>
          )}
      </>
    </>
  );
};

export default observer(JobApplicationDetails);
